import Sortable from 'sortablejs'

/**
 * Drag & drop for the roster duty-plan editor.
 *
 * Each matrix grid (role="table") is a SortableJS container. A Bereich row is the
 * draggable item (the bcard-* wrapper, a real subgrid box on desktop). Rows can be
 * reordered within their grid and — in manual mode, where grids share a sortable
 * group name — dragged into another group's grid. Both the order and the target
 * group are persisted server-side via reorderStands().
 *
 * SortableJS mutates the DOM directly; Livewire then re-renders the matrices on
 * every commit. We therefore tear down and rebuild all Sortable instances after
 * each commit (see the global 'roster-dnd-refresh' event dispatched in app.js).
 */
export default () => ({
  instances: [],

  init() {
    this._refresh = () => this.boot()
    window.addEventListener('roster-dnd-refresh', this._refresh)
    this.$nextTick(() => this.boot())
  },

  destroy() {
    window.removeEventListener('roster-dnd-refresh', this._refresh)
    this.teardown()
  },

  teardown() {
    this.instances.forEach(instance => {
      try { instance.destroy() } catch (e) { /* element already gone */ }
    })
    this.instances = []
  },

  boot() {
    this.teardown()

    this.bootGroupList()

    this.$root.querySelectorAll('[data-grid]').forEach(grid => {
      this.instances.push(new Sortable(grid, {
        group: grid.dataset.sortableGroup,
        draggable: '[data-stand-row]',
        handle: '[data-stand-drag]',
        forceFallback: true,
        // the detached fallback clone loses the grid context; CSS (.roster-drag-ghost)
        // hides its shift cells so the drag chip is just the row header
        fallbackClass: 'roster-drag-ghost',
        animation: 150,
        // keep rows out of the column-header region (headers are non-draggable
        // siblings at the top of the grid)
        onMove: evt => !(evt.related && evt.related.getAttribute('role') === 'columnheader'),
        onStart: evt => this.onDragStart(evt),
        onEnd: evt => { this.onDragEnd(); this.persist(evt) }
      }))
    })
  },

  /**
   * Track the pointer during a drag and flag the body with a not-allowed cursor
   * whenever the element under the pointer is not a valid drop target for the
   * dragged row (foreign grid in auto mode, the column-header band, or outside).
   */
  onDragStart(evt) {
    this._dragGroup = evt.from.dataset.sortableGroup
    this._onPointer = e => this.updateDropCursor(e)
    document.body.classList.add('roster-dragging')
    document.addEventListener('pointermove', this._onPointer, true)
    document.addEventListener('touchmove', this._onPointer, true)
  },

  onDragEnd() {
    document.removeEventListener('pointermove', this._onPointer, true)
    document.removeEventListener('touchmove', this._onPointer, true)
    document.body.classList.remove('roster-dragging', 'roster-nodrop')
  },

  updateDropCursor(e) {
    const point = e.touches ? e.touches[0] : e
    if (!point) { return }

    const el = document.elementFromPoint(point.clientX, point.clientY)
    document.body.classList.toggle('roster-nodrop', !this.isDropAllowed(el))
  },

  isDropAllowed(el) {
    if (!el || el.closest('[role="columnheader"]')) { return false }

    const grid = el.closest('[data-grid]')

    // a different grid is only a valid target when it shares the sortable group
    return !!grid && grid.dataset.sortableGroup === this._dragGroup
  },

  /**
   * The manual-group chip bar lives outside this component's $root (it is a
   * sibling of the duty-plan wrapper), so scope to the Livewire component root.
   */
  bootGroupList() {
    const scope = this.$root.closest('[wire\\:id]') || document

    scope.querySelectorAll('[data-group-list]').forEach(list => {
      this.instances.push(new Sortable(list, {
        draggable: '[data-group-chip]',
        handle: '[data-group-drag]',
        animation: 150,
        onEnd: () => {
          const ids = Array.from(list.querySelectorAll('[data-group-chip]'))
            .map(el => parseInt(el.dataset.groupId, 10))
          this.$wire.reorderGroups(ids)
        }
      }))
    })
  },

  persist(evt) {
    const promises = [this.persistGrid(evt.to)]
    if (evt.from !== evt.to) {
      promises.push(this.persistGrid(evt.from))
    }

    // Re-init once the server round-trip settles (a cross-grid move re-renders
    // the target matrix, invalidating the Sortable bound to the old DOM).
    Promise.all(promises).then(() => this.boot())
  },

  persistGrid(grid) {
    const groupId = grid.dataset.groupId ? parseInt(grid.dataset.groupId, 10) : null
    const ids = Array.from(grid.querySelectorAll('[data-stand-row]'))
      .map(el => parseInt(el.dataset.standId, 10))

    return this.$wire.reorderStands(groupId, ids)
  }
})
